/**
 * Admin › Access requests — the office queue for family app-access requests
 * (OMON-208 slice 4).
 *
 * A family member who signs into the Circle of Care app with a number that
 * matches no `_relatedPerson` mirror gets told "we've let the office know";
 * `family_bind` files a `_relatedPersonRequest` and emails support@. THIS
 * panel is where that promise is kept — without it the queue is written and
 * never read.
 *
 * Read path is the `related_person_action: list-requests` CALLABLE, not a
 * client-SDK query: `where(status == "pending")` is a broad list, exactly
 * the strict-rules-incompatible shape behind the stc_carer rollback
 * (OMON-250). Same rule as the family surface — the server resolves, the
 * browser renders.
 */

import { escapeHtml } from "../utils/html";
import { callRelatedPersonAction } from "./cf-client";
import {
  type AccessRequestRow, renderRequestList,
} from "./access-requests-view";

export { canGrantFromQueue, renderRequestList, renderRequestRow } from "./access-requests-view";
export type { AccessRequestRow } from "./access-requests-view";

export async function renderAdminAccessRequests(): Promise<void> {
  const host = document.getElementById("adminAccessRequestsList");
  if (!host) return;
  host.innerHTML = `<div class="admin-empty">Loading…</div>`;
  try {
    const result = await callRelatedPersonAction({ action: "list-requests" });
    const rows = (result?.requests ?? []) as AccessRequestRow[];
    host.innerHTML = renderRequestList(rows);
    host.querySelectorAll<HTMLElement>("[data-request-id]").forEach((rowEl) => {
      const id = rowEl.dataset.requestId!;
      const row = rows.find((r) => r.id === id);
      // ADR-0045 amendment 2026-09-21: the office's yes, from the queue.
      // One number can belong to several contacts (a relative of two
      // clients); each matched contact is granted, and the first call closes
      // the request atomically with its grant.
      rowEl.querySelector<HTMLButtonElement>("[data-request-grant]")?.addEventListener("click", async (ev) => {
        const btn = ev.currentTarget as HTMLButtonElement;
        const ids = row?.matchedRelatedPersonDocIds ?? [];
        if (ids.length === 0) return;
        if (!confirm(
          `Give this person access to the Circle of Support app?\n\n` +
          `Check what they typed against the client's records first. ` +
          (ids.length > 1
            ? `This number belongs to ${ids.length} contacts on file; all ${ids.length} get access. `
            : "") +
          `You can remove access again at any time from the client's contacts.`,
        )) return;
        btn.disabled = true;
        try {
          for (let i = 0; i < ids.length; i++) {
            await callRelatedPersonAction({
              action: "grant", relatedPersonDocId: ids[i],
              ...(i === 0 ? { requestId: id } : {}),
            });
          }
          await renderAdminAccessRequests();
        } catch (err) {
          btn.disabled = false;
          alert(`Could not give access: ${(err as Error).message}`);
        }
      });
      rowEl.querySelectorAll<HTMLButtonElement>("[data-request-action]").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const outcome = btn.dataset.requestAction as "resolved" | "rejected";
          const verb = outcome === "rejected" ? "Reject" : "Mark handled";
          // ☠️ This used to say access is granted by adding the person in
          // OneTouch and letting them sync — true until 2026-09-21, when a
          // synced number stopped being access (ADR-0045 amendment).
          if (!confirm(
            `${verb} this access request?\n\nThis closes it in the queue ` +
            `WITHOUT giving access. To let them in, add them to the client's ` +
            `contacts in OneTouch, then choose "Give Circle of Support access" ` +
            `on that contact.`,
          )) return;
          btn.disabled = true;
          try {
            await callRelatedPersonAction({
              action: "resolve-request", requestId: id, outcome,
            });
            await renderAdminAccessRequests();
          } catch (err) {
            btn.disabled = false;
            alert(`Could not update the request: ${(err as Error).message}`);
          }
        });
      });
    });
  } catch (err) {
    host.innerHTML =
      `<div class="admin-empty">Could not load access requests: ${escapeHtml((err as Error).message)}</div>`;
  }
}
